import { decryptPayload } from './crypto';
import { getOfflineOwnerIdentity } from './offlineIdentity';
import { decryptVaultPayload, encryptVaultPayload, getVaultStatus } from './vault';
import { commitVaultRecords, listVaultRecords, readVaultRecord } from './vaultStorage';

let running = null;

function assertOwner(owner) {
  if (getOfflineOwnerIdentity()?.ownerKey !== owner.ownerKey) throw new Error('Practice access changed during device migration. Existing records have been retained.');
}

function openExisting(name) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name);
    request.onupgradeneeded = () => { request.transaction.abort(); };
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => request.error?.name === 'AbortError' ? resolve(null) : reject(request.error);
    request.onblocked = () => reject(new Error('Close older Sena tabs before migrating device records.'));
  });
}

async function recordsFrom(db, store) {
  if (!db?.objectStoreNames.contains(store)) return [];
  return new Promise((resolve, reject) => {
    const request = db.transaction(store, 'readonly').objectStore(store).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

function replaceUnchanged(db, storeName, id, original, replacement, owner) {
  return new Promise((resolve, reject) => {
    let changed = false;
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.get(id);
    request.onsuccess = () => {
      if (getOfflineOwnerIdentity()?.ownerKey !== owner.ownerKey) { tx.abort(); return; }
      // An autosave or sync in another tab wins over this background migration.
      if (JSON.stringify(request.result) !== JSON.stringify(original)) return;
      store.put(replacement); changed = true;
    };
    tx.oncomplete = () => resolve(changed);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Device migration was interrupted. The original record was retained.'));
  });
}

export async function migrateOwnedLegacyOfflineRecords() {
  if (running) return running;
  running = (async () => {
    const report = { migrated: 0, retained: 0, failed: 0 };
    const owner = getOfflineOwnerIdentity();
    if (!owner || !(await getVaultStatus(owner)).unlocked) return report;
    const legacyOwner = `${owner.contextReferenceId}:${owner.userReferenceId}`;
    for (const source of [
      { database: 'vela-offline-queue', store: 'requests', id: 'id', payload: 'encrypted' },
      { database: 'vela-chart-drafts', store: 'drafts', id: 'draftId', payload: 'payload' },
    ]) {
      let db;
      try {
        db = await openExisting(source.database);
        for (const record of await recordsFrom(db, source.store)) {
          if (record.ownerKey !== legacyOwner && record.ownerKey !== owner.ownerKey) { report.retained += 1; continue; }
          if (record[source.payload]?.version === 2) continue;
          assertOwner(owner);
          try {
            let plaintext;
            if (record[source.payload]) plaintext = await decryptPayload(record[source.payload]);
            else if (source.store === 'requests') {
              const { id, createdAt, ownerKey, ...request } = record;
              plaintext = request;
            } else { report.failed += 1; continue; }
            if (!plaintext) { report.failed += 1; continue; }
            const encrypted = await encryptVaultPayload(plaintext, owner);
            // Verify the replacement before atomically exchanging the only copy.
            if (JSON.stringify(await decryptVaultPayload(encrypted, owner)) !== JSON.stringify(plaintext)) throw new Error('Device migration verification failed.');
            assertOwner(owner);
            const replacement = source.store === 'requests'
              ? { id: record.id, createdAt: record.createdAt, ownerKey: record.ownerKey, encrypted }
              : { ...record, payload: encrypted, clientLabel: 'Clinical chart' };
            if (await replaceUnchanged(db, source.store, record[source.id], record, replacement, owner)) report.migrated += 1;
          } catch { report.failed += 1; }
        }
      } finally { db?.close(); }
    }
    for (const record of await listVaultRecords('snapshots')) {
      if (record.ownerKey !== owner.ownerKey || record.payload?.version === 2) continue;
      try {
        assertOwner(owner);
        const plaintext = await decryptPayload(record.payload);
        const encrypted = await encryptVaultPayload(plaintext, owner);
        await decryptVaultPayload(encrypted, owner);
        const current = await readVaultRecord('snapshots', record.id);
        if (JSON.stringify(current) !== JSON.stringify(record)) continue;
        assertOwner(owner);
        await commitVaultRecords([{ store: 'snapshots', expected: record, record: { ...record, payload: encrypted } }]);
        report.migrated += 1;
      } catch { report.failed += 1; }
    }
    // The shared key may still be the only way to recover another owner's or
    // an unidentified legacy record. Never delete it as a side effect here.
    window.dispatchEvent(new CustomEvent('vela:offline-legacy-migration', { detail: report }));
    return report;
  })();
  try { return await running; } finally { running = null; }
}
